/**
 * Host Photo Client
 * Used by Edit terminal to download photos from the Bridge host.
 */

import { getEffectiveBridgeBaseUrl } from '../config/terminal-urls';

export interface HostPhotoInfo {
  sequence: number;
  filename: string;
  url?: string;
}

interface ListHostPhotosResponse {
  success: boolean;
  transactionId: string;
  folderName: string;
  photos: HostPhotoInfo[];
}

/**
 * List photos for a transaction from the Bridge host.
 * Returns an empty array when not found (404) to simplify fallback handling.
 */
export async function listHostPhotos(
  transactionId: string
): Promise<HostPhotoInfo[]> {
  const baseUrl = getEffectiveBridgeBaseUrl();

  const response = await fetch(
    `${baseUrl}/storage/transactions/${encodeURIComponent(
      transactionId
    )}/photos`
  );

  if (!response.ok) {
    if (response.status === 404) {
      return [];
    }
    const err = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(err.message || 'Failed to list photos from host');
  }

  const data = (await response.json()) as ListHostPhotosResponse;
  if (!data.success || !Array.isArray(data.photos)) {
    return [];
  }
  return data.photos;
}

/**
 * Download a single photo from the Bridge host as a Blob.
 */
export async function downloadHostPhoto(
  transactionId: string,
  sequence: number
): Promise<Blob> {
  const baseUrl = getEffectiveBridgeBaseUrl();

  const response = await fetch(
    `${baseUrl}/storage/transactions/${encodeURIComponent(
      transactionId
    )}/photos/${sequence}`
  );

  if (!response.ok) {
    const errText = await response.text().catch(() => response.statusText);
    throw new Error(errText || 'Failed to download photo from host');
  }

  return response.blob();
}

